import { Component, useEffect, useRef, useState, type ReactNode } from "react";

declare const __BUILD_ID__: string;

/**
 * Automatic updates and recovery, on for branded sites (Buildwise). The app checks /version.json;
 * when a new version is out it counts down and reloads itself, and nobody can put it off. It waits while a window (like a form) is open.
 * If the app crashes it reloads a fresh copy by itself. Only the page files are cleared, so
 * anything saved on this device (open sales, sales waiting to send) stays.
 */
const ON = !!import.meta.env.VITE_BRAND && import.meta.env.PROD;
const CHECK_EVERY = 5 * 60_000;
const SECONDS = 30;
const TRIED = "update-tried";
const JUST = "update-just-done";
const TRIES = "recover-tries";
const CRASH = "last-crash";

/** A form window is open: don't reload under it. */
const isBusy = () => !!document.querySelector('[role="dialog"]');
const hidden = () => false;
const clearPageFiles = async () => {
  try {
    await Promise.all((await caches.keys()).filter((k) => /shell/.test(k)).map((k) => caches.delete(k)));
  } catch {
    /* no cache to clear */
  }
};
const reloadFresh = async () => {
  try {
    await (await navigator.serviceWorker?.getRegistration())?.update();
  } catch {
    /* reload anyway */
  }
  window.location.reload();
};

export function AppUpdate() {
  const [next, setNext] = useState<{ id: string; notes: string[] } | null>(null);
  const [count, setCount] = useState(SECONDS);
  const [waiting, setWaiting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [done, setDone] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (!ON || started.current) return;
    started.current = true;
    if (sessionStorage.getItem(JUST) === __BUILD_ID__) {
      sessionStorage.removeItem(JUST);
      setDone(true);
      setTimeout(() => setDone(false), 4000);
    }
    const check = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
        const v = (await res.json()) as { id?: string; notes?: string[] };
        // Don't loop if a reload keeps landing on the old version: wait for the next check to clear.
        if (v.id && v.id !== __BUILD_ID__ && Number(sessionStorage.getItem(`${TRIED}:${v.id}`) ?? 0) < 3) setNext({ id: v.id, notes: v.notes ?? [] });
      } catch {
        /* offline: check again later */
      }
    };
    void check();
    const t = setInterval(() => void check(), CHECK_EVERY);
    const again = () => void check();
    window.addEventListener("online", again);
    window.addEventListener("focus", again);
    return () => {
      clearInterval(t);
      window.removeEventListener("online", again);
      window.removeEventListener("focus", again);
    };
  }, []);

  const apply = () => {
    if (!next) return;
    setUpdating(true);
    const key = `${TRIED}:${next.id}`;
    sessionStorage.setItem(key, String(Number(sessionStorage.getItem(key) ?? 0) + 1));
    sessionStorage.setItem(JUST, next.id);
    void reloadFresh();
  };

  useEffect(() => {
    if (!next || updating) return;
    const t = setInterval(() => {
      const busy = isBusy();
      setWaiting(busy);
      if (busy) return;
      setCount((c) => {
        if (c <= 1) apply();
        return Math.max(0, c - 1);
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [next, updating]);

  if (!ON) return null;
  if (!next && done) return <div role="status" className="fixed bottom-5 left-1/2 z-[70] w-[min(460px,92%)] -translate-x-1/2 rounded-2xl bg-ink px-4 py-3 text-sm font-semibold text-white print:hidden">Updated to the latest version</div>;
  if (!next || hidden()) return null;
  if (waiting && !updating) return <div role="status" className="fixed bottom-4 right-4 z-[70] rounded-full bg-ink px-4 py-2.5 text-[13px] font-bold text-white shadow-lg print:hidden">Update waits until you close this window</div>;
  return (
    <div role="status" className="fixed bottom-5 right-5 z-[70] w-[min(380px,calc(100%-32px))] rounded-2xl bg-white p-4 text-ink shadow-[0_16px_40px_rgba(11,21,23,0.28)] print:hidden">
      {updating ? (
        <b className="block text-[15px]">Updating…</b>
      ) : (
        <>
          <b className="block text-[15px]">Updating in {count} second{count === 1 ? "" : "s"}</b>
          <p className="mt-1 text-[13px] text-muted">Save any changes now. The page will reload by itself.</p>
          {next.notes.length ? <ul className="mt-2 list-disc pl-[18px] text-[13px] leading-relaxed">{next.notes.map((n) => <li key={n}>{n}</li>)}</ul> : null}
          <button type="button" onClick={apply} className="mt-3 h-11 rounded-[10px] bg-brand px-4 text-sm font-extrabold text-white hover:bg-brand-dark">Update now</button>
        </>
      )}
    </div>
  );
}

/** What shows while the app repairs itself. It keeps trying, so nobody has to do anything. */
function Recovering() {
  const [offline, setOffline] = useState(!navigator.onLine);
  const tries = Number(sessionStorage.getItem(TRIES) ?? 0);
  useEffect(() => {
    const retry = () => {
      sessionStorage.setItem(TRIES, String(Number(sessionStorage.getItem(TRIES) ?? 0) + 1));
      void clearPageFiles().then(reloadFresh);
    };
    if (!navigator.onLine) {
      const back = () => {
        setOffline(false);
        retry();
      };
      window.addEventListener("online", back);
      return () => window.removeEventListener("online", back);
    }
    const t = setTimeout(retry, tries < 3 ? 1500 : 60_000);
    return () => clearTimeout(t);
  }, [tries]);
  return (
    <div role="status" className="flex min-h-dvh items-center justify-center bg-graphite px-5 py-8 text-white">
      <div className="flex w-full max-w-[560px] flex-col gap-4">
        <h1 className="text-4xl font-black tracking-tight">{offline ? "Waiting for the internet" : "Fixing the back office"}</h1>
        <p className="text-base leading-relaxed text-white/80">{offline ? "The back office needs the internet to get a fresh copy. It will come back by itself." : tries < 3 ? `Reloading a fresh copy by itself. Try ${tries + 1} of 3.` : "It's taking longer than usual. It will keep trying by itself."}</p>
        <p className="rounded-xl bg-white/10 px-3.5 py-3 text-sm text-white/85">Your saved work is safe. No one needs to do anything.</p>
        {sessionStorage.getItem(CRASH) ? <p className="break-words font-mono text-xs text-white/50">What went wrong: {sessionStorage.getItem(CRASH)}</p> : null}
      </div>
    </div>
  );
}

export class Recovery extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  private calm = 0;
  static getDerivedStateFromError() {
    return { failed: ON };
  }
  componentDidCatch(error: unknown, info: { componentStack?: string | null }) {
    // Keep a short note of the crash so it shows on the repair screen and can be reported.
    const where = info.componentStack?.split("\n").map((l) => l.trim()).find(Boolean) ?? "";
    const what = error instanceof Error ? error.message : String(error);
    try {
      sessionStorage.setItem(CRASH, `${what.slice(0, 200)} ${where.slice(0, 80)} · ${window.location.pathname}`);
    } catch {
      /* no storage */
    }
    console.error("Back office crashed:", error, info.componentStack);
  }
  componentDidMount() {
    (window as unknown as { __appStarted?: boolean }).__appStarted = true;
    // A minute without trouble: forget the earlier tries.
    this.calm = window.setTimeout(() => {
      sessionStorage.removeItem(TRIES);
      sessionStorage.removeItem("boot-tries");
      sessionStorage.removeItem(CRASH);
    }, 60_000);
  }
  componentWillUnmount() {
    clearTimeout(this.calm);
  }
  render() {
    return this.state.failed ? <Recovering /> : this.props.children;
  }
}
